import React, { useEffect } from 'react';
import Head from 'next/head';

import { useOasApp } from '@/providers/oas-app-provider';

import { PhysicianData } from '../profile/profile.types';
import usePersonSchema from './hooks/usePersonSchema';
import usePhysicianSchema from './hooks/usePhysicianSchema';

const SCHEMA_SCRIPT_ID = 'physicianSchemaScript';

export const PhysicianSchema: React.FC<PhysicianData> = (physicianData) => {
  const { hostApp, scSiteCookie } = useOasApp();
  const isDotCMS = hostApp === 'dotcms' || scSiteCookie === 'dotcms';

  // Initialize data from hooks
  const physicianSchema = usePhysicianSchema(physicianData);
  const personSchema = usePersonSchema(physicianData);

  const memoizedScriptBody = React.useMemo(() => {
    const scriptBody = {
      '@context': 'https://schema.org',
      '@graph': [physicianSchema, personSchema],
    };

    return JSON.stringify(scriptBody);
  }, [physicianSchema, personSchema]);

  // For dotCMS environments: use direct DOM manipulation
  useEffect(() => {
    if (!isDotCMS) return;

    // Remove existing script if present
    const existingScript = document.getElementById(SCHEMA_SCRIPT_ID);
    if (existingScript) {
      existingScript.remove();
    }

    // Create and append new script element
    const script = document.createElement('script');
    script.id = SCHEMA_SCRIPT_ID;
    script.type = 'application/ld+json';
    script.textContent = memoizedScriptBody;
    document.head.appendChild(script);

    // Cleanup on unmount
    return () => {
      const scriptToRemove = document.getElementById(SCHEMA_SCRIPT_ID);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [isDotCMS, memoizedScriptBody]);

  // For Sitecore/Next.js environments: use Next.js Head component
  if (!isDotCMS) {
    return (
      <Head>
        <script
          id={SCHEMA_SCRIPT_ID}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: memoizedScriptBody }}
        />
      </Head>
    );
  }

  // For dotCMS: the useEffect handles DOM manipulation, so return null
  return null;
};
